// Copyright 2025, University of Maryland, All Rights Reserved
import type { Pipeline, PipelineOption, PipelineStatus } from '@/subsystems/pipelines'
import type { DashboardDatasetConfig } from '../models'

type PipelineCardStatus = 'idle' | 'queued' | 'running' | 'completed' | 'error'

export interface PipelineCard {
  id: string
  label: string
  description?: string | null
  status: PipelineCardStatus
  updatedAt?: string | null
  findingsDatasetKey?: string | null
  /** Agent owning the findings dataset — required to address its results. */
  findingsAgentId?: string | null
}

/**
 * Filter available datasets to only those belonging to a specific pipeline.
 *
 * Uses the explicit `pipelineId` field on each dataset configuration.
 * Datasets with empty/unset pipelineId are shown for all pipelines.
 *
 * @param datasets All available datasets from the workspace
 * @param pipelineId The pipeline ID to filter for (e.g., "financial-contract-review")
 * @returns Datasets that have matching pipelineId or no pipelineId set
 */
export function filterDatasetsByPipeline(
  datasets: DashboardDatasetConfig[],
  pipelineId: string | null | undefined
): DashboardDatasetConfig[] {
  if (!pipelineId) {
    return datasets
  }

  // Include datasets that match the pipeline OR have no pipeline set (global datasets)
  return datasets.filter(ds => !ds.pipelineId || ds.pipelineId === pipelineId)
}

/**
 * Transform pipeline data from API (dict) to array of options for UI.
 *
 * Pipelines with `hidden: true` are dropped — they're admin-only and
 * shouldn't appear in user-facing dashboards.
 */
export function transformPipelinesToOptions(
  pipelinesData: Record<string, Pipeline> | null
): PipelineOption[] {
  if (!pipelinesData) return []
  return Object.entries(pipelinesData)
    .filter(([, pipeline]) => !pipeline.hidden)
    .map(([id, pipeline]) => ({
      id,
      label: pipeline.name || id,
      description: pipeline.description,
    }))
}

/**
 * Normalize raw status string to valid PipelineCardStatus.
 */
function normalizeStatus(raw: string | undefined): PipelineCardStatus {
  const lower = raw?.toLowerCase() ?? 'idle'
  if (lower === 'completed' || lower === 'running' || lower === 'queued' || lower === 'error') {
    return lower
  }
  return 'idle'
}

/**
 * Build pipeline cards from available pipelines, statuses, and datasets.
 * Pure function that combines pipeline options with their current statuses.
 */
export function buildPipelineCards(
  availablePipelines: PipelineOption[],
  pipelineStatuses: PipelineStatus[],
  availableDatasets: DashboardDatasetConfig[]
): PipelineCard[] {
  const statusMap = new Map(pipelineStatuses.map(s => [s.pipelineId, s]))

  return availablePipelines.map(p => {
    const status = statusMap.get(p.id)

    // Find findings dataset for this pipeline
    const findingsDataset = availableDatasets.find(
      ds => ds.pipelineId === p.id && ds.schemaCatalogId === 'contract_findings'
    )

    return {
      id: p.id,
      label: p.label,
      description: p.description,
      status: normalizeStatus(status?.status),
      updatedAt: status?.updatedAt,
      findingsDatasetKey: findingsDataset?.datasetKey ?? null,
      findingsAgentId: findingsDataset?.agentId ?? null,
    }
  })
}
